import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, TrendingUp, Landmark, RefreshCw, Check, FileCode2, ExternalLink, FileDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();

const CONFIDENCE_STYLE = {
  high: "bg-emerald-100 text-emerald-800 border-emerald-200",
  medium: "bg-amber-100 text-amber-800 border-amber-200",
  low: "bg-rose-100 text-rose-800 border-rose-200",
};

const PLANNING_STATUS_STYLE = {
  Permitted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Conditions: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Rejected: "bg-rose-100 text-rose-800 border-rose-200",
  Withdrawn: "bg-rose-100 text-rose-800 border-rose-200",
  Undecided: "bg-amber-100 text-amber-800 border-amber-200",
  Referred: "bg-amber-100 text-amber-800 border-amber-200",
  Unresolved: "bg-amber-100 text-amber-800 border-amber-200",
  Unknown: "bg-muted text-muted-foreground border-border",
};

function formatGBP(n) {
  if (n === null || n === undefined) return "—";
  return `£${Number(n).toLocaleString("en-GB")}`;
}

/** "2025-03-28" -> "28/03/2025". Falls back to the raw string if unparsable. */
function formatDMY(isoDate) {
  if (!isoDate) return "";
  const parts = String(isoDate).slice(0, 10).split("-");
  if (parts.length !== 3) return isoDate;
  const [y, m, d] = parts;
  return `${d}/${m}/${y}`;
}

function formatGeneratedAt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

/**
 * Agent/Master admin property edit → "Estimate Price" CTA. Calls AI with
 * real HM Land Registry sold comps, shows an estimated range + reasoning +
 * the comparables it used. The report is persisted server-side — opening
 * the modal shows the last report instantly; a new one is only generated
 * when the user explicitly clicks "Regenerate" (or none exists yet).
 *
 * Pass `onUsePrice(value)` to receive the estimate's midpoint when the user
 * clicks "Use This Price". Master admin uses it to fill the Price field;
 * agents (who can't edit price at all) use it purely as a confirmation
 * toast — the caller decides what, if anything, to do with the value.
 */
export function EstimatePriceModal({ propertyId, open, onOpenChange, onUsePrice }) {
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const generate = useCallback(async () => {
    try {
      const { data } = await axios.post(`${API}/property-admin/property/${propertyId}/estimate-price`, {}, { withCredentials: true });
      setResult(data);
      setError(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to estimate price";
      setError(msg);
      toast.error(msg);
    }
  }, [propertyId]);

  useEffect(() => {
    if (!open || !propertyId) return;
    setResult(null);
    setError(null);
    setLoading(true);
    axios
      .get(`${API}/property-admin/property/${propertyId}/estimate-price`, { withCredentials: true })
      .then(({ data }) => setResult(data))
      .catch(async (e) => {
        if (e?.response?.status === 404) {
          // No report yet — generate the first one automatically.
          await generate();
        } else {
          const msg = e?.response?.data?.detail || e?.message || "Failed to load estimate";
          setError(msg);
          toast.error(msg);
        }
      })
      .finally(() => setLoading(false));
  }, [open, propertyId, generate]);

  const handleRegenerate = async () => {
    setRegenerating(true);
    await generate();
    setRegenerating(false);
  };

  const handleUsePrice = () => {
    if (!result || !onUsePrice) return;
    const mid = Math.round((result.estimated_low + result.estimated_high) / 2 / 500) * 500;
    onUsePrice(mid);
    onOpenChange(false);
  };

  const handleDownloadPdf = () => {
    window.location.href = `${API}/property-admin/property/${propertyId}/estimate-price/pdf`;
  };

  const soldComps = result?.comparables || [];
  const busy = loading || regenerating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto overflow-x-hidden slh-dialog-fixed-90" data-testid="estimate-price-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <TrendingUp className="h-5 w-5 text-emerald-600 shrink-0" />
            AI Price Estimate
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            Based on real HM Land Registry sold prices for this postcode, plus general UK market knowledge.
          </DialogDescription>
        </DialogHeader>

        {busy && (
          <div className="flex flex-col items-center justify-center py-10 sm:py-12 gap-3" data-testid="estimate-price-loading">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground text-center px-4">
              {regenerating ? "Regenerating…" : "Looking up sold prices in the area and reasoning about a fair range…"}
            </p>
          </div>
        )}

        {!busy && error && (
          <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md p-4" data-testid="estimate-price-error">
            {error}
          </div>
        )}

        {!busy && result && (
          <div className="space-y-4 sm:space-y-5" data-testid="estimate-price-result">
            <div className="flex items-start justify-between flex-wrap gap-3 border rounded-lg p-3 sm:p-4 bg-muted/30">
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground mb-1">Estimated range</p>
                <p className="text-xl sm:text-2xl font-bold break-words" data-testid="estimate-price-range">
                  {formatGBP(result.estimated_low)} – {formatGBP(result.estimated_high)}
                </p>
                {result.generated_at && (
                  <p className="text-[11px] text-muted-foreground mt-1" data-testid="estimate-price-generated-at">
                    Generated {formatGeneratedAt(result.generated_at)}
                  </p>
                )}
              </div>
              <Badge
                className={`border shrink-0 ${CONFIDENCE_STYLE[result.confidence] || CONFIDENCE_STYLE.low}`}
                data-testid="estimate-price-confidence"
              >
                {(result.confidence || "low").toUpperCase()} CONFIDENCE
              </Badge>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={handleRegenerate}
                data-testid="estimate-price-regenerate-btn"
              >
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                Regenerate
              </Button>
              {result.document_id && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={handleDownloadPdf}
                  data-testid="estimate-price-download-pdf-btn"
                >
                  <FileDown className="mr-1.5 h-3.5 w-3.5" />
                  Download PDF
                </Button>
              )}
              {onUsePrice && (
                <Button
                  type="button"
                  size="sm"
                  className="text-xs"
                  onClick={handleUsePrice}
                  data-testid="estimate-price-use-btn"
                >
                  <Check className="mr-1.5 h-3.5 w-3.5" />
                  Use This Price
                </Button>
              )}
            </div>

            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Reasoning</p>
              <p className="text-sm leading-relaxed" data-testid="estimate-price-reasoning">{result.reasoning}</p>
            </div>

            {soldComps.length > 0 ? (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase mb-2 flex items-center gap-1.5">
                  <Landmark className="h-3.5 w-3.5 shrink-0" />
                  Sold comparables ({soldComps.length}) — HM Land Registry
                </p>
                <div className="space-y-1.5" data-testid="estimate-price-sold-comps">
                  {soldComps.map((c, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 text-sm border rounded-md px-2.5 sm:px-3 py-2">
                      <span className="truncate min-w-0 flex-1">{c.address}</span>
                      <span className="shrink-0 text-muted-foreground text-xs">{formatDMY(c.date)}</span>
                      <span className="shrink-0 font-medium">{formatGBP(c.price)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">
                No direct sold comparables were found for this postcode — this estimate relies on general UK market knowledge only.
              </p>
            )}

            {Array.isArray(result.planning_applications) && result.planning_applications.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase mb-2 flex items-center gap-1.5">
                  <FileCode2 className="h-3.5 w-3.5 shrink-0" />
                  Nearby planning applications ({result.planning_applications.length}) — PlanIt
                </p>
                <div className="space-y-1.5" data-testid="estimate-price-planning-apps">
                  {result.planning_applications.map((a, i) => (
                    <a
                      key={i}
                      href={a.link || undefined}
                      target="_blank"
                      rel="noreferrer"
                      className="block border rounded-md px-2.5 sm:px-3 py-2 text-sm hover:bg-muted/30 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="truncate min-w-0 flex-1 font-medium">{a.address}</span>
                        <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border ${PLANNING_STATUS_STYLE[a.status] || PLANNING_STATUS_STYLE.Unknown}`}>
                          {a.status}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{a.description}</p>
                      <div className="flex items-center gap-1 text-[11px] text-muted-foreground mt-1">
                        {formatDMY(a.date)}
                        {a.link && <ExternalLink className="h-3 w-3 ml-1" />}
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default EstimatePriceModal;
